
import { useState, useEffect } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('contact');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const subject = encodeURIComponent(`New message from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );

    window.location.href = `mailto:hicham.elmefeddel01@gmail.com?subject=${subject}&body=${body}`;
  };

  const socialLinks = [
    { icon: 'ri-github-line', label: 'GitHub', url: 'https://github.com/hel-mefe', color: 'hover:text-green-400' },
    { icon: 'ri-linkedin-line', label: 'LinkedIn', url: 'https://linkedin.com/in/hicham-elmefeddel', color: 'hover:text-blue-400' },
    { icon: 'ri-twitter-line', label: 'X (Twitter)', url: 'https://x.com/helmefeddel', color: 'hover:text-cyan-400' },
    { icon: 'ri-mail-line', label: 'Email', url: 'mailto:hicham.elmefeddel01@gmail.com', color: 'hover:text-red-400' }
  ];

  return (
    <section id="contact" className="py-20 px-4 relative overflow-hidden">
      {/* Animated circuit pattern background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full opacity-5" viewBox="0 0 100 100">
          <defs>
            <pattern id="circuit" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 0 10 L 10 10 L 10 0 M 10 10 L 20 10 M 10 10 L 10 20" stroke="#22c55e" strokeWidth="0.5" fill="none">
                <animate attributeName="stroke-opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite" />
              </path>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`}>
          <div className="inline-block bg-gray-900/80 border border-green-500/30 rounded-lg p-4 mb-8 backdrop-blur-md hover-glow">
            <div className="text-green-400 text-left">
              <span className="text-green-500 animate-pulse">$</span> ./contact.sh --init
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 gradient-text">Get In Touch</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto mb-8 animate-pulse"></div>
          <p className="text-gray-300 max-w-2xl mx-auto hover:text-green-300 transition-colors duration-300">
            Let's discuss your next project, collaborate on innovative solutions, or just talk about technology and systems programming.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className={`transition-all duration-1000 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-8 backdrop-blur-sm hover-glow">
              <h3 className="text-2xl font-bold text-green-400 mb-6 flex items-center">
                <i className="ri-terminal-line mr-2 animate-pulse"></i>
                Send Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-green-400 text-sm flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:border-green-500/60 focus:outline-none transition-all duration-300 hover:border-green-500/50 text-sm"
                    placeholder="Your name"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-green-400 text-sm flex items-center">
                    <i className="ri-mail-line mr-2"></i>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-black/50 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:border-green-500/60 focus:outline-none transition-all duration-300 hover:border-green-500/50 text-sm"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-green-400 text-sm flex items-center">
                    <i className="ri-message-line mr-2"></i>
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full bg-black/50 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:border-green-500/60 focus:outline-none transition-all duration-300 hover:border-green-500/50 resize-none text-sm"
                    placeholder="Tell me about your project or just say hello..."
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-400 py-3 rounded-lg border border-green-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 whitespace-nowrap cursor-pointer hover-scale animate-float flex items-center justify-center"
                >
                  <i className="ri-send-plane-line mr-2 animate-pulse"></i>
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
            {/* Social Links */}
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-8 backdrop-blur-sm hover-glow">
              <h3 className="text-2xl font-bold text-green-400 mb-6 flex items-center">
                <i className="ri-links-line mr-2 animate-pulse"></i>
                Connect With Me
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    className={`flex items-center p-4 bg-black/30 border border-green-500/20 rounded-lg transition-all duration-300 hover:border-green-500/50 ${link.color} hover-scale cursor-pointer animate-float`}
                    style={{ animationDelay: `${0.9 + index * 0.1}s` }}
                  >
                    <i className={`${link.icon} text-2xl mr-3 animate-pulse`}></i>
                    <span className="text-gray-300 hover:text-current transition-colors duration-300">{link.label}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-8 backdrop-blur-sm hover-glow">
              <h3 className="text-2xl font-bold text-green-400 mb-6 flex items-center">
                <i className="ri-information-line mr-2 animate-pulse"></i>
                Quick Info
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center text-gray-300 hover:text-green-400 transition-colors duration-300">
                  <i className="ri-map-pin-line text-green-500 mr-3 animate-pulse"></i>
                  <span>Available for remote work</span>
                </div>
                <div className="flex items-center text-gray-300 hover:text-green-400 transition-colors duration-300">
                  <i className="ri-time-line text-green-500 mr-3 animate-pulse"></i>
                  <span>Usually responds within 24 hours</span>
                </div>
                <div className="flex items-center text-gray-300 hover:text-green-400 transition-colors duration-300">
                  <i className="ri-code-line text-green-500 mr-3 animate-pulse"></i>
                  <span>Open to interesting projects</span>
                </div>
              </div>
            </div>

            {/* Terminal Status */}
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm hover-glow">
              <div className="text-green-400 text-sm space-y-2">
                <div className="flex items-center">
                  <span className="text-green-500">$</span>
                  <span className="ml-2">status --check</span>
                </div>
                <div className="pl-4 space-y-1">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-gray-300">Available for new projects</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-gray-300">Response time: &lt; 24h</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-gray-300">Currently coding in: C++</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`text-center mt-16 pt-8 border-t border-green-500/20 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`} style={{ animationDelay: '1.2s' }}>
          <p className="text-gray-400 hover:text-green-400 transition-colors duration-300">
            Built by <span className="text-green-400 font-semibold">@hel-mefe</span>, Always available for work, feel free to reach out!
          </p>
        </div>
      </div>
    </section>
  );
}
